import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import {
  X, ChevronRight, Sparkles, MessageCircle,
  FileCode, FolderGit, Database, CheckCircle
} from 'lucide-react'

interface OnboardingStep {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  action?: { label: string; href: string }
}

const onboardingSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to HalCode!',
    description: 'Your AI-powered coding assistant. Let\'s get you started with a quick tour of the platform.',
    icon: <Sparkles className="w-6 h-6 text-primary-400" />
  },
  {
    id: 'chat',
    title: 'Start Chatting',
    description: 'Ask coding questions, request code reviews, or get help with bugs. The AI understands your context.',
    icon: <MessageCircle className="w-6 h-6 text-blue-400" />,
    action: { label: 'Try it now', href: '/chat' }
  },
  {
    id: 'code-editor',
    title: 'Built-in Code Editor',
    description: 'Edit files directly in your browser with full syntax highlighting and IntelliSense support.',
    icon: <FileCode className="w-6 h-6 text-green-400" />,
    action: { label: 'Open editor', href: '/code-editor' }
  },
  {
    id: 'git',
    title: 'Git Integration',
    description: 'Clone repositories, make changes, and push back to GitHub - all without leaving HalCode.',
    icon: <FolderGit className="w-6 h-6 text-orange-400" />,
    action: { label: 'Connect repo', href: '/git' }
  },
  {
    id: 'mcp',
    title: 'MCP Servers',
    description: 'Extend HalCode capabilities with Model Context Protocol servers for databases, APIs, and more.',
    icon: <Database className="w-6 h-6 text-purple-400" />,
    action: { label: 'Explore servers', href: '/mcp-servers' }
  }
]

export default function OnboardingPopup() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isVisible, setIsVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [hasCompleted, setHasCompleted] = useState(false)

  useEffect(() => {
    if (user?.id) {
      const onboardingKey = `halcode-onboarding-${user.id}`
      const completed = localStorage.getItem(onboardingKey)
      if (!completed) {
        // Show onboarding after a short delay
        const timer = setTimeout(() => {
          setIsVisible(true)
        }, 1000)
        return () => clearTimeout(timer)
      } else {
        setHasCompleted(true)
      }
    }
  }, [user?.id])

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      handleComplete()
    }
  }

  const handleSkip = () => {
    if (user?.id) {
      localStorage.setItem(`halcode-onboarding-${user.id}`, 'completed')
    }
    setIsVisible(false)
    setHasCompleted(true)
  }

  const handleComplete = () => {
    if (user?.id) {
      localStorage.setItem(`halcode-onboarding-${user.id}`, 'completed')
    }
    setIsVisible(false)
    setHasCompleted(true)
  }

  const handleAction = () => {
    const action = onboardingSteps[currentStep].action
    if (action) {
      setIsVisible(false)
      navigate(action.href)
    } else {
      handleNext()
    }
  }

  if (!isVisible || hasCompleted) return null

  const step = onboardingSteps[currentStep]
  const progress = ((currentStep + 1) / onboardingSteps.length) * 100

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Popup */}
      <div className="relative bg-dark-800 border border-dark-700 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Progress Bar */}
        <div className="h-1 bg-dark-700">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-1 text-dark-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-dark-700 flex items-center justify-center mb-4">
            {step.icon}
          </div>

          {/* Step Indicator */}
          <p className="text-xs text-primary-400 font-medium mb-2">
            Step {currentStep + 1} of {onboardingSteps.length}
          </p>

          {/* Title & Description */}
          <h2 className="text-xl font-semibold text-white mb-2">{step.title}</h2>
          <p className="text-dark-400 text-sm mb-6">{step.description}</p>

          {/* Action Button */}
          {step.action ? (
            <button
              onClick={handleAction}
              className="w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
            >
              {step.action.label}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
            >
              {currentStep === onboardingSteps.length - 1 ? 'Get Started' : 'Next'}
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {/* Skip Link */}
          <button
            onClick={handleSkip}
            className="w-full mt-3 text-xs text-dark-500 hover:text-dark-300 transition-colors"
          >
            Skip tour
          </button>
        </div>

        {/* Step Dots */}
        <div className="flex items-center justify-center gap-2 pb-4">
          {onboardingSteps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentStep
                  ? 'bg-primary-500'
                  : idx < currentStep
                  ? 'bg-primary-500/50'
                  : 'bg-dark-600'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// Component to show quick tips for new users (smaller, less intrusive)
export function QuickTip({ id, title, children, onDismiss }: {
  id: string
  title: string
  children: React.ReactNode
  onDismiss?: () => void
}) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const dismissed = localStorage.getItem(`halcode-tip-${id}`)
    if (!dismissed) {
      // Show tip after a delay
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [id])

  const handleDismiss = () => {
    setIsVisible(false)
    if (onDismiss) onDismiss()
    localStorage.setItem(`halcode-tip-${id}`, 'dismissed')
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm bg-dark-800 border border-dark-700 rounded-xl shadow-xl animate-slide-up">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-primary-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-dark-200 mb-1">{title}</h4>
            <div className="text-xs text-dark-400">{children}</div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 text-dark-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
